import {
  CheckinCycleNotification,
  NotificationAnchor,
  NotificationKind,
  OffsetDirection,
  RecipientGroup,
} from './types'
import { labelFromStoredTime, toTwentyFourHour } from './form-helpers'

// The notification cadence rule as held in the builder's working state. Mirrors
// CheckinCycleNotification but in camelCase, with `sendTime` as a "9 AM" display
// label. `id` is the server id for persisted rules, or a `new-*` / `default-*`
// placeholder for ones not yet saved (the backend treats any non-numeric id as
// new). Persisted alongside the cycle via the create/update payload.
export type NotificationDraft = {
  id: string
  kind: NotificationKind
  name: string
  anchor: NotificationAnchor
  offsetDirection: OffsetDirection
  offsetDays: number
  sendTime: string | null
  sendTimezone: string | null
  recipientGroups: RecipientGroup[]
  additionalEmails: string[]
  emailSubject: string
  emailBody: string
  enabled: boolean
  position: number
}

// Map a cycle's serialized notification snapshot into the builder's working shape.
export const draftFromSerialized = (
  notification: CheckinCycleNotification,
): NotificationDraft => ({
  id: notification.id,
  kind: notification.kind,
  name: notification.name,
  anchor: notification.anchor,
  offsetDirection: notification.offset_direction,
  offsetDays: notification.offset_days,
  sendTime: notification.send_time
    ? labelFromStoredTime(notification.send_time)
    : '9 AM',
  sendTimezone: notification.send_timezone,
  recipientGroups: notification.recipient_groups,
  additionalEmails: notification.additional_emails ?? [],
  emailSubject: notification.email_subject ?? '',
  emailBody: notification.email_body ?? '',
  enabled: notification.enabled,
  position: notification.position,
})

// Serialize a draft into the snake_case attributes the API expects. `send_time`
// and `send_timezone` only apply to before/after-event rules. `additional_emails`
// is only carried when the additional-recipients group is selected.
export const serializeNotification = (
  draft: NotificationDraft,
  index: number,
  fallbackTimezone: string,
): Record<string, unknown> => {
  const usesOffset = draft.offsetDirection !== 'on_event'

  return {
    id: draft.id,
    kind: draft.kind,
    name: draft.name.trim(),
    anchor: draft.anchor,
    offset_direction: draft.offsetDirection,
    offset_days: usesOffset ? draft.offsetDays : 0,
    send_time: usesOffset ? toTwentyFourHour(draft.sendTime ?? '9 AM') : null,
    send_timezone: usesOffset ? (draft.sendTimezone ?? fallbackTimezone) : null,
    recipient_groups: draft.recipientGroups,
    additional_emails: draft.recipientGroups.includes('additional_recipients')
      ? draft.additionalEmails
      : [],
    email_subject: draft.emailSubject.trim(),
    email_body: draft.emailBody,
    enabled: draft.enabled,
    position: index,
  }
}
